/**
 * Admin "view-as-persona" PREVIEW provider (#1383).
 *
 * Powers the admin workspace switcher's faithful preview: while previewing-as a
 * persona, the whole UI masks down to exactly what that persona's REAL login
 * would render (sidebar nav, page visibility, money figures, admin-only
 * surfaces). The simulated flag/role/preset definition is loaded ONCE from the
 * admin-only /config/permission-presets/ endpoint, which is derived server-side
 * from the canonical PRESET_FLAGS -- so the preview can never drift from a real
 * login.
 *
 * CRITICAL HONESTY: this is a FRONTEND visibility preview, NOT a server-side
 * privilege reduction. The admin's real token still holds full privileges; the
 * simulation only masks the UI so the admin can SEE each persona's experience.
 * No server guard is weakened.
 *
 * Gating: ONLY an admin can ever be in a preview (the switcher is admin-only,
 * #1263). If the user is not an admin -- or logs out -- any persisted preview is
 * cleared, so a non-admin can never be stuck masked.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { getPermissionPresets } from "../api/client";
import type { PreviewPersona } from "../api/client";
import { useAuth } from "./auth";
import {
  clearPreviewPersona,
  getPreviewPersona,
  setPreviewPersona,
} from "../lib/previewPersona";
import type { PreviewPersonaKey } from "../lib/previewPersona";
import { PreviewPersonaContext } from "./previewPersona";

export function PreviewPersonaProvider({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const isAdmin = user?.role === "admin";

  // Initialise from storage so a reload keeps the preview (read synchronously).
  const [persona, setPersona] = useState<PreviewPersonaKey | null>(() =>
    getPreviewPersona(),
  );
  const [personaMap, setPersonaMap] = useState<Record<
    string,
    PreviewPersona
  > | null>(null);

  // Load the canonical preset->flags map once, for admins only (it is an
  // admin-only endpoint). Non-admins never preview, so they never need it.
  useEffect(() => {
    if (loading) return; // wait until auth is known
    if (!isAdmin) {
      setPersonaMap(null);
      return;
    }
    const controller = new AbortController();
    getPermissionPresets(controller.signal)
      .then((cfg) => setPersonaMap(cfg.personas))
      .catch(() => {
        // On failure leave the map null: previewing simply does not mask (the
        // switcher still navigates -- the #1369 wayfinding floor). Never throws.
      });
    return () => controller.abort();
  }, [isAdmin, loading]);

  // SAFETY: a non-admin (or logged-out) user can never be in a preview. Clear
  // any persisted preview the moment the user is not an admin.
  useEffect(() => {
    if (loading) return; // auth not yet known -- do not wipe a valid preview
    if (!isAdmin && persona !== null) {
      setPersona(null);
      clearPreviewPersona();
    }
  }, [isAdmin, persona, loading]);

  const enterPreview = useCallback(
    (next: PreviewPersonaKey) => {
      if (!isAdmin) return; // admins only
      setPersona(next);
      setPreviewPersona(next);
    },
    [isAdmin],
  );

  const exitPreview = useCallback(() => {
    setPersona(null);
    clearPreviewPersona();
  }, []);

  // Only treat as previewing when an admin has an active persona. The active
  // definition is the simulated bundle from the loaded map.
  const previewing = isAdmin && persona !== null;
  const active = previewing && personaMap ? (personaMap[persona] ?? null) : null;

  const value = useMemo(
    () => ({
      persona: previewing ? persona : null,
      previewing,
      active,
      enterPreview,
      exitPreview,
    }),
    [persona, previewing, active, enterPreview, exitPreview],
  );

  return (
    <PreviewPersonaContext.Provider value={value}>
      {children}
    </PreviewPersonaContext.Provider>
  );
}
